import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VouchersService from '../../../Api/VouchersService';
import { toast } from 'react-toastify';

const AddVouchers = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const voucherId = new URLSearchParams(location.search).get('id');
    const voucherAPI = new VouchersService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        code: '',
        voucher_type: 'percentage',
        discount_value: '',
        product_type: 'course',
        usage_limit: '',
        valid_until: ''
    });
    const [validationErrors, setValidationErrors] = useState({
        code: '',
        discount_value: ''
    });

    useEffect(() => {
        const fetchVoucher = async () => {
            if (voucherId) {
                try {
                    setIsLoading(true);
                    const response = await voucherAPI.GetById(voucherId);
                    if (response.data && response.data.data) {
                        const voucherData = response.data.data;
                        setFormData({
                            code: voucherData.code || '',
                            voucher_type: voucherData.voucher_type || 'percentage',
                            discount_value: voucherData.discount_value || '',
                            product_type: voucherData.product_type || 'course',
                            usage_limit: voucherData.usage_limit || '',
                            valid_until: voucherData.valid_until ? new Date(voucherData.valid_until).toISOString().split('T')[0] : ''
                        });
                    }
                } catch (err) {
                    setError('Failed to load voucher data. Please try again.');
                    console.error('Error loading voucher:', err);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchVoucher();
    }, [voucherId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear validation errors when user starts typing
        setValidationErrors(prev => ({
            ...prev,
            [name]: ''
        }));

        // Real-time validation
        if (name === 'code') {
            const codeRegex = /^[A-Z0-9]{4,12}$/;
            if (value && !codeRegex.test(value.toUpperCase())) {
                setValidationErrors(prev => ({
                    ...prev,
                    code: 'Code must be 4-12 uppercase alphanumeric characters'
                }));
            }
        }

        if (name === 'discount_value') {
            const discountValue = parseFloat(value);
            if (formData.voucher_type === 'percentage' && discountValue > 100) {
                setValidationErrors(prev => ({
                    ...prev,
                    discount_value: 'Percentage discount cannot exceed 100%'
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Validate voucher code (4-12 uppercase alphanumeric characters)
            const codeRegex = /^[A-Z0-9]{4,12}$/;
            if (!codeRegex.test(formData.code)) {
                throw new Error('Voucher code must be 4-12 uppercase alphanumeric characters');
            }

            // Validate discount value for percentage type
            if (formData.voucher_type === 'percentage' && (formData.discount_value <= 0 || formData.discount_value > 100)) {
                throw new Error('Percentage discount must be between 1 and 100');
            }

            // Validate discount value for fixed type
            if (formData.voucher_type === 'fixed' && formData.discount_value <= 0) {
                throw new Error('Fixed discount value must be greater than 0');
            }

            const submissionData = {
                ...formData,
                code: formData.code.toUpperCase(), // Ensure code is uppercase
                valid_until: new Date(formData.valid_until).toISOString()
            };

            let response;
            if (voucherId) {
                // Update existing voucher
                response = await voucherAPI.UpdateByID(submissionData, voucherId);
            } else {
                // Create new voucher
                response = await voucherAPI.AddVouchers(submissionData);
            }

            if (response.data.status === "success") {
                toast.success(voucherId ? 'Voucher updated successfully!' : 'Voucher created successfully!');
                navigate('/vouchers-list');
            } else {
                throw new Error(response.data.message || 'Failed to save voucher');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to save voucher');
            toast.error(error.response?.data?.message || error.message || 'An error occurred while saving');
            console.error('Voucher save error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <h4 className="text-xl text-primary-light mb-24">
                    {voucherId ? 'Edit Voucher' : 'Add New Voucher'}
                </h4>
                {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {error}
                    </div>
                )}
                {isLoading && !formData.code ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Code <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${validationErrors.code ? 'is-invalid' : ''}`}
                                        name="code"
                                        value={formData.code}
                                        onChange={handleChange}
                                        placeholder="Enter voucher code"
                                        required
                                    />
                                    {validationErrors.code && (
                                        <div className="invalid-feedback">
                                            {validationErrors.code}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Voucher Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        name="voucher_type"
                                        value={formData.voucher_type}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Discount Value <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className={`form-control ${validationErrors.discount_value ? 'is-invalid' : ''}`}
                                        name="discount_value"
                                        value={formData.discount_value}
                                        onChange={handleChange}
                                        placeholder="Enter discount value"
                                        required
                                    />
                                    {validationErrors.discount_value && (
                                        <div className="invalid-feedback">
                                            {validationErrors.discount_value}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Product Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        name="product_type"
                                        value={formData.product_type}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="all">All</option>
                                        <option value="course">Course</option>
                                        <option value="addonPack">Addon Pack</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Usage Limit <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="usage_limit"
                                        value={formData.usage_limit}
                                        onChange={handleChange}
                                        placeholder="Enter usage limit"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Valid Until <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="valid_until"
                                        value={formData.valid_until}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                            <button
                                type="button"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {voucherId ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    voucherId ? 'Update Voucher' : 'Save Voucher'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddVouchers;
